import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { castDraft, produce } from 'immer';

import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../../reducers';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createViewActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  | 'changeInspectedTrackerId'
  | 'moveTrackersToSample'
  | 'replaceSample'
  | 'removeTrackersFromSample'
  | 'removeAllTrackersFromSample'
  | 'selectTrackers'
  | 'deselectTrackers'
  | 'updateCompleteSelection'
  | 'deselectAllTrackers'
  | 'changeSelectedCols'
> => ({
  // inspected tracker actions
  changeInspectedTrackerId: (id) => {
    set(
      produce((s: DraftState) => {
        s.trackers.inspectedTrackerId = id;
      }),
    );

    if (id != null) {
      apiClient
        .getTracker(id)
        .then((tracker) => {
          const oldTracker = get().trackers.items.get(id.toHexString());
          if (oldTracker == null) return;

          set(
            produce((s: DraftState) => {
              s.trackers.items.set(id.toHexString(), { ...castDraft(oldTracker), ...tracker });
            }),
          );
        })
        .catch((err) => {
          console.error(err);
        });
    }
  },

  // sample actions
  moveTrackersToSample: (ids) => {
    set(
      produce((s: DraftState) => {
        ids.forEach((id) => s.trackers.sampleTrackerIds.add(id.toHexString()));
      }),
    );
  },
  replaceSample: (ids) => {
    set(
      produce((s: DraftState) => {
        s.trackers.sampleTrackerIds = new Set(ids.map((id) => id.toHexString()));
      }),
    );
  },
  removeTrackersFromSample: (ids) => {
    set(
      produce((s: DraftState) => {
        ids.forEach((id) => s.trackers.sampleTrackerIds.delete(id.toHexString()));
      }),
    );
  },
  removeAllTrackersFromSample: () => {
    set(
      produce((s: DraftState) => {
        s.trackers.sampleTrackerIds = new Set();
      }),
    );
  },

  // selection actions
  selectTrackers: (ids, selectionId) => {
    set(
      produce((s: DraftState) => {
        ids.forEach((id) => s.trackers.selectedTrackerIds[selectionId].add(id.toHexString()));
      }),
    );
  },
  deselectTrackers: (ids, selectionId) => {
    set(
      produce((s: DraftState) => {
        ids.forEach((id) => s.trackers.selectedTrackerIds[selectionId].delete(id.toHexString()));
      }),
    );
  },
  updateCompleteSelection: (ids, selectionId) => {
    set(
      produce((s: DraftState) => {
        s.trackers.selectedTrackerIds[selectionId] = new Set(ids);
      }),
    );
  },
  deselectAllTrackers: (selectionId) => {
    set(
      produce((s: DraftState) => {
        s.trackers.selectedTrackerIds[selectionId] = new Set();
      }),
    );
  },

  changeSelectedCols: (newCols) => {
    set(
      produce((s: DraftState) => {
        s.trackers.selectedCols = newCols;
      }),
    );
  },
});
